import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import type { UniversalSearchResult } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useAuth } from '@context/AuthContext';
import { FormSearchInput } from '../UI';
import {
    getUniversalSearchResultDetails,
    getUniversalSearchResultKey,
    getUniversalSearchResultPath,
    groupUniversalSearchResults,
    isSearchableUniversalSearchTerm
} from './universalSearch';

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_LIST_ID = 'universalSearchResults';

const getOptionId = (index: number) => `${RESULTS_LIST_ID}-option-${index}`;

interface SearchOverlayProps {
    onClose: () => void;
}

/** Full-screen header search. Mounted only while open, so its state resets each time it opens. */
const SearchOverlay = ({ onClose }: SearchOverlayProps) => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTerm(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const isSearchable = isSearchableUniversalSearchTerm(debouncedTerm);

    const searchQuery = useAppQuery({
        ...trpc.search.universal.queryOptions({ term: debouncedTerm }),
        enabled: isSearchable,
        placeholderData: keepPreviousData,
        staleTime: 30_000
    });

    const results = useMemo(() => (isSearchable ? (searchQuery.data?.results ?? []) : []), [isSearchable, searchQuery.data]);
    const groups = useMemo(() => groupUniversalSearchResults(results), [results]);

    // Highlight the top result whenever a new result set arrives, so Enter opens the best match.
    useEffect(() => {
        setHighlightedIndex(0);
    }, [results]);

    // Keep the highlighted row visible while arrowing through a long list.
    useEffect(() => {
        document.getElementById(getOptionId(highlightedIndex))?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    const openResult = (result: UniversalSearchResult) => {
        onClose();
        void navigate(getUniversalSearchResultPath(result));
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                onClose();
                break;
            case 'ArrowDown':
                if (results.length > 0) {
                    e.preventDefault();
                    setHighlightedIndex((index) => (index + 1) % results.length);
                }

                break;
            case 'ArrowUp':
                if (results.length > 0) {
                    e.preventDefault();
                    setHighlightedIndex((index) => (index - 1 + results.length) % results.length);
                }

                break;
            case 'Enter': {
                const result = results[highlightedIndex];

                if (result) {
                    e.preventDefault();
                    openResult(result);
                }

                break;
            }
            default:
                break;
        }
    };

    const handleBackgroundClick = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const isLoading = isSearchable && searchQuery.isFetching;
    const showNoMatches = isSearchable && searchQuery.isSuccess && !searchQuery.isPlaceholderData && results.length === 0;

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center bg-black/95 pt-20" onClick={handleBackgroundClick}>
            <div className="absolute top-8 right-8">
                <button aria-label="Close search" className="text-4xl text-white hover:text-zinc-300" type="button" onClick={onClose}>
                    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-4.146-3.146a.5.5 0 0 0-.708-.708L8 7.293 4.854 4.146a.5.5 0 1 0-.708.708L7.293 8l-3.147 3.146a.5.5 0 0 0 .708.708L8 8.707l3.146 3.147a.5.5 0 0 0 .708-.708L8.707 8l3.147-3.146z"
                            fillRule="evenodd"
                        />
                    </svg>
                </button>
            </div>
            <div className="w-full max-w-[800px] px-8 py-4">
                <div className="mx-auto mb-4 w-full text-center">
                    <h3 className="text-2xl text-white">
                        {isAuthenticated
                            ? 'Search stocks, indexes, crypto, strategies, portfolios, and more.'
                            : 'Type in any stock/crypto symbol, or company name.'}
                    </h3>
                    {isAuthenticated && (
                        <p className="mt-2 text-sm text-zinc-400">
                            Enter an ID like <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-200">42</kbd>, or narrow it with a
                            prefix: <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-200">s42</kbd> strategy,{' '}
                            <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-200">p42</kbd> portfolio,{' '}
                            <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-200">e42</kbd> experiment.
                        </p>
                    )}
                </div>
                <div className="relative w-full">
                    <FormSearchInput
                        showSearchIcon
                        aria-activedescendant={results.length > 0 ? getOptionId(highlightedIndex) : undefined}
                        aria-autocomplete="list"
                        aria-controls={RESULTS_LIST_ID}
                        aria-expanded={results.length > 0}
                        autoComplete="off"
                        className="w-full"
                        id="quickSearch"
                        name="q"
                        placeholder="Search"
                        ref={searchInputRef}
                        role="combobox"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                    />
                    {isLoading && (
                        <div className="absolute top-1/2 right-4 -translate-y-1/2">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}
                    <div className="absolute right-0 left-0 z-10 mt-1 overflow-hidden rounded-md bg-zinc-900">
                        {searchQuery.isError && isSearchable && (
                            <div className="p-4 text-red-400" role="alert">
                                Search failed: {searchQuery.error.message}
                            </div>
                        )}
                        {showNoMatches && <div className="p-4 text-zinc-400">No matches for “{debouncedTerm}”.</div>}
                        {results.length > 0 && (
                            <ul aria-label="Search results" className="max-h-[70vh] overflow-y-auto" id={RESULTS_LIST_ID} role="listbox">
                                {groups.map((group) => (
                                    <li key={group.key} role="presentation">
                                        <div className="bg-zinc-950 px-4 py-1.5 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
                                            {group.label}
                                        </div>
                                        <ul role="presentation">
                                            {group.items.map(({ result, index }) => {
                                                const details = getUniversalSearchResultDetails(result);
                                                const isHighlighted = index === highlightedIndex;

                                                return (
                                                    <li
                                                        aria-selected={isHighlighted}
                                                        id={getOptionId(index)}
                                                        key={getUniversalSearchResultKey(result)}
                                                        role="option"
                                                        onMouseEnter={() => setHighlightedIndex(index)}
                                                    >
                                                        <Link
                                                            className={`block border-b border-zinc-800 px-4 py-3 ${isHighlighted ? 'bg-zinc-700' : 'bg-zinc-900'}`}
                                                            tabIndex={-1}
                                                            to={getUniversalSearchResultPath(result)}
                                                            onClick={onClose}
                                                        >
                                                            <div className="flex items-baseline justify-between gap-4">
                                                                <span className="truncate text-lg font-semibold text-white">
                                                                    {details.title}
                                                                </span>
                                                                <span className="shrink-0 text-sm text-zinc-400">{details.kindLabel}</span>
                                                            </div>
                                                            {(details.subtitle || details.badges.length > 0) && (
                                                                <div className="mt-1 flex items-center justify-between gap-4 text-sm text-zinc-400">
                                                                    <span className="truncate">{details.subtitle}</span>
                                                                    <span className="flex shrink-0 gap-1.5">
                                                                        {details.badges.map((badge) => (
                                                                            <span
                                                                                className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs text-zinc-300"
                                                                                key={badge}
                                                                            >
                                                                                {badge}
                                                                            </span>
                                                                        ))}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchOverlay;
